'use client';

import Link from 'next/link';
import { useSubscriptionStatus } from '../hooks/use-billing';

/** Fereastra de notificare înainte de expirarea grantului «firmă nouă» (zile). */
const GRANT_NOTICE_DAYS = 30;

/**
 * Banner sticky-top pentru ultimele 30 de zile din grantul «firmă nouă»
 * (plan `free_new_company_2y` — Facturare PRO gratuit până firma împlinește
 * 2 ani). După expirare contul trece automat pe planul Free permanent —
 * banner-ul previne «zidul surpriză».
 *
 * Stil identic cu TrialBanner (coerență): bulină · etichetă · „N zile rămase" ·
 * bară de progres (din fereastra de 30 de zile de notificare) · „— apoi planul
 * Free" · buton „Alege un plan". Accentele comută amber → roșu sub 7 zile.
 * NU dismissable.
 */
export function NewCompanyGrantBanner() {
  const { data, isLoading } = useSubscriptionStatus();

  if (isLoading || !data) return null;

  const { plan, daysUntilExpire } = data.subscription;

  if (plan !== 'free_new_company_2y') return null;
  if (daysUntilExpire === null || daysUntilExpire === undefined) return null;
  if (daysUntilExpire > GRANT_NOTICE_DAYS) return null;
  if (daysUntilExpire <= 0) return null; // expirat: entitlement-ul a trecut deja contul pe free

  const isUrgent = daysUntilExpire <= 7;
  const elapsed = Math.min(GRANT_NOTICE_DAYS, Math.max(0, GRANT_NOTICE_DAYS - daysUntilExpire));
  const pct = Math.min(100, Math.max(0, Math.round((elapsed / GRANT_NOTICE_DAYS) * 100)));
  const daysLabel = daysUntilExpire === 1 ? 'o zi' : `${daysUntilExpire} zile`;

  const tone = isUrgent
    ? {
        wrap: 'bg-red-50 border-red-200 text-red-900 dark:bg-red-950/40 dark:border-red-800 dark:text-red-100',
        dot: 'bg-red-500',
        track: 'bg-red-200 dark:bg-red-900/60',
        fill: 'bg-red-600',
        muted: 'text-red-700 dark:text-red-300/80',
        btn: 'bg-red-600 text-white hover:bg-red-700',
      }
    : {
        wrap: 'bg-amber-50 border-amber-200 text-amber-900 dark:bg-amber-950/40 dark:border-amber-800 dark:text-amber-100',
        dot: 'bg-amber-500',
        track: 'bg-amber-200 dark:bg-amber-900/60',
        fill: 'bg-amber-500',
        muted: 'text-amber-700 dark:text-amber-300/80',
        btn: 'bg-amber-500 text-white hover:bg-amber-600',
      };

  return (
    <div className={`border-b px-3 py-2 text-sm ${tone.wrap}`} role="status" aria-live="polite">
      <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-x-3 gap-y-1.5">
        <div className="flex min-w-0 items-center gap-2.5">
          <span
            className={`inline-flex h-2 w-2 shrink-0 rounded-full ${tone.dot} ${isUrgent ? 'animate-pulse' : ''}`}
            aria-hidden="true"
          />
          <span className="whitespace-nowrap">
            <span className="font-semibold">Facturare PRO gratuit</span>
            <span className="opacity-50"> · </span>
            <span className="font-bold">{daysLabel}</span> rămase
          </span>
          {/* bară de progres — cât din fereastra de notificare s-a consumat */}
          <span
            className={`hidden h-1.5 w-20 shrink-0 overflow-hidden rounded-full sm:block ${tone.track}`}
            role="progressbar"
            aria-valuenow={pct}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={`${pct}% din fereastra de notificare`}
          >
            <span className={`block h-full rounded-full ${tone.fill}`} style={{ width: `${pct}%` }} />
          </span>
          <span className={`hidden whitespace-nowrap md:inline ${tone.muted}`}>— apoi planul Free</span>
        </div>
        <Link
          href="/setari?tab=billing"
          className={`shrink-0 rounded-lg px-3.5 py-1.5 text-xs font-semibold shadow-sm transition-colors ${tone.btn}`}
        >
          Alege un plan
        </Link>
      </div>
    </div>
  );
}
